import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { API_URL, pathVideoCall } from "../../config/constants";
import socket from "../../socket";
import { formatSlot } from "../caregiverChat";
import { consultationTone } from "../videoConsultationPanel";
import JoinConsultationModal from "../joinConsultationModal";
import {
  Card, Title, Status, Dot, When, JoinButton, SupportButton,
} from "./styles";

/**
 * Próxima videoconsulta del usuario, en la barra lateral. Sirve a los dos
 * lados: el terapeuta ve con qué paciente es y el paciente con qué terapeuta.
 * `hideSupportLink` lo oculta cuando ya se está en esa pantalla.
 */
const NextConsultationCard = ({ role, onGoToSupport, hideSupportLink = false }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [session, setSession] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [joinOpen, setJoinOpen] = useState(false);

  const load = useCallback(() => {
    fetch(`${API_URL}/sessions/upcoming`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((r) => r.json())
      .then((data) => setSession(Array.isArray(data) ? data[0] || null : null))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    socket.on("sessionUpdated", load);
    return () => socket.off("sessionUpdated", load);
  }, [load]);

  // El paso de "Confirmed" a "Ready to join" depende del reloj, no de un evento.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  if (!session) return null;

  const isTherapist = role === "therapist";
  const withName = isTherapist ? session.patient_code : session.therapist_name;
  const tone = consultationTone(session, now);

  // Mismo arranque que desde Caregiver Support: el terapeuta llama y el
  // paciente entra a esperar (ver PatientCaregiverSupport).
  const join = () => {
    setJoinOpen(false);
    if (isTherapist) {
      history.push(pathVideoCall, {
        patient: { id: session.patient_id, label: session.patient_code || "" },
        sessionId: session.id,
      });
    } else {
      history.push(pathVideoCall, { waitForSessionId: session.id });
    }
  };

  return (
    <Card>
      <Title>{t("consultation.nextWith", { name: withName || "" })}</Title>
      <Status>
        <Dot $tone={tone} />
        {t(`consultation.status.${tone}`)}
      </Status>
      <When>{formatSlot(session.scheduled_at)}</When>

      <JoinButton disabled={tone !== "ready"} onClick={() => setJoinOpen(true)}>
        {t("consultation.join")}
      </JoinButton>
      {!hideSupportLink && (
        <SupportButton onClick={() => onGoToSupport(session)}>
          {t("consultation.readyModal.back")}
        </SupportButton>
      )}

      {joinOpen && (
        <JoinConsultationModal
          session={session}
          withLabel={withName || ""}
          roleLabel={t(`consultation.readyModal.${isTherapist ? "rolePatient" : "roleTherapist"}`)}
          onClose={() => setJoinOpen(false)}
          onJoin={join}
        />
      )}
    </Card>
  );
};

export default NextConsultationCard;
